import { useMemo } from "react";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { useGetDataQuery } from "@/utils/react-query-hooks/getData";

/**
 * Hierarchical (parent → child) annotation bar graph.
 *
 * Counts come from the BE via aggrMetricsAcrossDimensions on the annotations
 * collection (1 dim, child tags only — a 2-dim parent×child aggregation
 * cross-products since both are arrays on an annotation). Parent grouping is
 * done on the FE using each child tag's own `tags.<parent>.data` parent refs.
 *
 * Used by:
 * - SM tenants (`subThemes` → `tags`)
 * - Tenant plugins (gsl: `subThemes` → `tags`)
 */
export const useHierarchicalAnnotationBarGraphData = ({
  enabled,
  activeFilters,
  selectedTPLsArrayOptions,
  parent,
  child,
}) => {
  const mergedActiveFilters = useMemo(
    () => [
      {
        filterId: "contentTypeFilter_contentType",
        values: selectedTPLsArrayOptions,
        target: { filterType: "valuePathType", valuePath: "meta.contentType" },
      },
      ...(activeFilters || []),
    ],
    [activeFilters, selectedTPLsArrayOptions]
  );

  const {
    data: aggrResponse,
    status: aggrStatus,
    error: aggrError,
    isLoading: aggrLoading,
  } = useAggrMetricsAcrossDimensions({
    contentType: "annotations",
    dimensions: [`annotations.tags.${child}`],
    activeFilters: mergedActiveFilters,
    queryOptions: { enabled: enabled && !!child },
  });

  const childDocsQuery = useGetDataQuery({
    contentTypes: child ? [child] : [],
    findQuery: { kp_published_status: "published" },
    projection: { tagId: 1, "main.title": 1, tags: 1 },
    queryOptions: { enabled: enabled && !!child },
  });

  const parentDocsQuery = useGetDataQuery({
    contentTypes: parent ? [parent] : [],
    findQuery: { kp_published_status: "published" },
    projection: { tagId: 1, "main.title": 1, "meta.taxonomyRole": 1 },
    queryOptions: { enabled: enabled && !!parent },
  });

  const data = useMemo(() => {
    const childCounts = aggrResponse?.data || aggrResponse || [];
    if (!Array.isArray(childCounts) || childCounts.length === 0) return [];

    const childDocs = childDocsQuery.data?.data || [];
    const parentDocs = parentDocsQuery.data?.data || [];
    if (childDocs.length === 0 || parentDocs.length === 0) return [];

    const childDocById = new Map();
    childDocs.forEach((c) => childDocById.set(String(c._id), c));

    const parentMetaById = new Map();
    parentDocs.forEach((p) =>
      parentMetaById.set(String(p._id), {
        tagId: p.tagId,
        title: p.main?.title,
        taxonomyRole: p.meta?.taxonomyRole || null,
      })
    );

    // Group child tag rows by their parent(s). A child with multiple parents
    // appears under each — matches prior plugin/SM-fork behaviour.
    const groups = {};
    childCounts.forEach((row) => {
      const childDoc = childDocById.get(String(row.value));
      const childDisplay = childDoc?.main?.title || row.display;
      const count = row.count || 0;
      if (!count) return;

      const parents = childDoc?.tags?.[parent]?.data || [];
      if (parents.length === 0) return;

      parents.forEach((p) => {
        const parentId = String(p._id);
        const parentMeta = parentMetaById.get(parentId);
        if (!parentMeta) return;

        if (!groups[parentId]) {
          groups[parentId] = {
            category: parentMeta.title,
            tagCategory: parentMeta.tagId,
            taxonomyRole: parentMeta.taxonomyRole,
            total: 0,
            tags: [],
          };
        }
        groups[parentId].total += count;
        groups[parentId].tags.push({ display: childDisplay, value: count });
      });
    });

    return Object.values(groups)
      .sort((a, b) => b.total - a.total)
      .flatMap((g) =>
        [...g.tags]
          .sort((a, b) => b.value - a.value)
          .map((tag) => ({
            category: g.category,
            tagCategory: g.tagCategory,
            taxonomyRole: g.taxonomyRole,
            display: tag.display,
            value: tag.value,
          }))
      );
  }, [aggrResponse, childDocsQuery.data, parentDocsQuery.data, parent]);

  const status = (() => {
    const parts = [aggrStatus, childDocsQuery.status, parentDocsQuery.status];
    if (parts.some((s) => s === "error")) return "error";
    if (parts.some((s) => s === "loading")) return "loading";
    return aggrStatus;
  })();

  return {
    data,
    status,
    error: aggrError || childDocsQuery.error || parentDocsQuery.error,
    isLoading: aggrLoading || childDocsQuery.isLoading || parentDocsQuery.isLoading,
  };
};
